<template>
  <div>
    <CRow>
      <CCol lg="12">
        <h1 class="main-header mb-1" v-translate translate-context="accounting.movements">
          Movements
        </h1>
        <p class="text-help-dark" v-translate translate-context="accounting.movements">Register of accounting movements</p>

        <CCard>
          <CCardHeader class="border-0 ml-3">
            <div class="d-flex align-items-center font-weight-medium">
              <CIcon name="cipShoppingBag01" size="custom-size" :width="18" />
              <span class="mx-1" v-translate translate-context="accounting.movements">All movements</span>
              <span>({{ movements.pagination.total|number }})</span>
            </div>

            <div class="card-header-actions">
              <PButton
                  class="btn btn-sm btn-primary mr-3"
                  @click="onExport"
                  :loading="exporting"
                  :loading-text="$gettext('Generating file...')"
                  spinner-color="secondary"
                  :disabled="!movements.results || movements.results.length <= 0"
                  v-if="hasScopePerm('accounting.movements.export')"
              >
                <CIcon width="30" name="cipFileDownload02" class="mr-2"/>
                <translate translate-context="accounting.movements">Export</translate>
              </PButton>
            </div>
          </CCardHeader>

          <CCardBody class="px-0">
            <AjaxTable
                v-if="isReady"
                ref="movementsTable"
                :fields="fields"
                :data="movements"
                :fetch="fetchList"
                table-name="accounting-movements"
                :context-key="contextKey"
                :sync-url="true"
                class="custom-table custom-table--clean custom-table--has-calendar mx-4"
            >
              <template #effective_date="{item}">
                <td>
                  {{ item.effective_date|formatDateTime('L') }}
                </td>
              </template>

              <template #transaction_type="{item}">
                <td>
                  <CBadge class="badge-outline" :color="getTypeColor(item.transaction_type)" v-c-tooltip="item.refund_reason || ''">
                    <div class="d-flex align-items-center">
                      <CIcon :name="getTypeIcon(item.transaction_type)" size="custom-size" :width="14" class="mr-2" />
                      <span class="font-weight-normal">{{ getTypeLabel(item.transaction_type) }}</span>
                    </div>
                  </CBadge>
                </td>
              </template>

              <template #event_name="{item}">
                <td>
                  <template v-if="item.pica_services && item.pica_services.length">
                    <template v-for="(ps, idx) in item.pica_services">
                      <router-link
                        :key="ps.id"
                        :to="{ name: 'picaServiceRedirect', params: { picaServiceId: ps.id } }"
                        class="reference-link"
                        @click.native.stop
                      >{{ ps.event_name }}</router-link><span v-if="idx < item.pica_services.length - 1" :key="'sep-ps-' + idx">, </span>
                    </template>
                  </template>
                  <span v-else-if="item.event_name">{{ item.event_name }}</span>
                  <span v-else class="text-muted">-</span>
                </td>
              </template>

              <template #payment_method="{item}">
                <td>
                  <span v-if="item.payment_method">{{ item.payment_method }}</span>
                  <span v-else class="text-muted">-</span>
                </td>
              </template>

              <template #total_amount="{item}">
                <td class="text-right">
                  <span>{{item.total_amount|currency(item.currency)}}</span>
                </td>
              </template>

              <template #reference="{item}">
                <td>
                 <CLink @click="navigateToSource(item)" class="link--order">
                  <span v-if="item.order_number">
                    {{ item.order_number }}
                  </span>
                  <span v-else-if="item.context && item.context.order_number">
                    {{ item.context.order_number }}
                  </span>
                  <span v-else class="text-muted">
                    #{{ item.source_id }}
                  </span>
                 </CLink>
                </td>
              </template>

              <template #actions="{item}">
                <td class="text-right">
                   <CButton color="dark"
                           variant="outline"
                           class="btn-circle btn-circle--sm d-inline-flex align-items-center justify-content-center"
                           size="sm"
                           @click="navigateToSource(item)">
                    <CIcon name="cipArrowRight"/>
                  </CButton>
                </td>
              </template>
            </AjaxTable>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import PButton from '@/components/PButton.vue'
import AjaxTable from '@/domain/core/components/AjaxTable.vue'
import {
  MOVEMENT_TYPE_LABELS,
  MOVEMENT_TYPE_COLORS,
  MOVEMENT_TYPE_ICON,
  MOVEMENT_TYPES,
  getMovementDetailRouteParams
} from '../constants'
import {timeZone} from "../../../utils/date";
import ConfigScope from "@/domain/core/constant/configScope";

export default {
  name: 'MovementList',
  components: {
    PButton,
    AjaxTable
  },
  props: {
    scopeType: {
      type: String,
      required: true,
      validator: v => ['contract-manager', 'pica-service'].includes(v)
    },
    scopeId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      isReady: false,
      exporting: false
    }
  },
  async mounted() {
    await this.setScope(this.scope)

    await this.fetchScopedConfig()

    this.isReady = true
  },
  computed: {
    timeZone() {
      return timeZone
    },
    ...mapGetters('accounting', ['movements']),

    scope() {
      return {
        type: this.scopeType,
        id: this.scopeId ? parseInt(this.scopeId) : null
      }
    },

    contextKey() {
      return `${this.scopeType}-${this.scopeId}`
    },

    movementTypeOptions() {
      return [
        { value: '', label: this.$pgettext('accounting.movements', 'All') },
        { value: MOVEMENT_TYPES.ORDER_ITEM, label: MOVEMENT_TYPE_LABELS[MOVEMENT_TYPES.ORDER_ITEM] },
        { value: MOVEMENT_TYPES.REFUND, label: MOVEMENT_TYPE_LABELS[MOVEMENT_TYPES.REFUND] }
      ]
    },

    fields() {
      const fields = [
        {
          key: 'effective_date',
          label: this.$pgettext('accounting.movements', 'Date'),
          _style: 'width: 140px;',
          sorter: true,
          dateRangeFrom: 'date_from',
          dateRangeTo: 'date_to'
        },
        {
          key: 'reference',
          label: this.$pgettext('accounting.movements', 'Reference'),
          _style: 'width: 170px;',
          sorter: false,
          filter: false
        },
        this.scopeType === 'contract-manager' ? {
          key: 'event_name',
          label: this.$pgettext('accounting.movements', 'Event'),
          sorter: false
        } : null,
        {
          key: 'payment_method',
          label: this.$pgettext('accounting.movements', 'Payment'),
          _style: 'width: 130px;',
          sorter: false,
          filter: false
        },
        {
          key: 'total_amount',
          label: this.$pgettext('accounting.movements', 'Amount'),
          _style: 'width: 130px;',
          sorter: true,
          filter: false
        },
        {
          key: 'transaction_type',
          label: this.$pgettext('accounting.movements', 'Type'),
          _style: 'width: 140px;',
          sorter: false,
          options: this.movementTypeOptions
        },
        {
          key: 'actions',
          label: '',
          _style: 'width: 80px;',
          sorter: false,
          filter: false
        }
      ]
      return fields.filter(Boolean)
    }
  },
  methods: {
    ...mapActions('accounting', [
      'setScope',
      'fetchMovementList',
      'exportMovements'
    ]),
    ...mapActions('login', ['fetchConfig']),

    async fetchScopedConfig() {
      try {
        if (this.scopeType === 'pica-service') {
          await this.fetchConfig({
            type: 'accounting_pica_service',
            id: this.scopeId,
            scope: ConfigScope.PICA_SERVICE
          })
        } else if (this.scopeType === 'contract-manager') {
          await this.fetchConfig({
            type: 'accounting_contract_manager',
            id: this.scopeId
          })
        }
      } catch (e) {
        // Config endpoint may not exist yet, continue without it
      }
    },

    hasScopePerm(permission) {
      if (!this.scope || !this.scope.type) return false

      let fullPermission
      if (this.scope.type === 'pica-service') {
        fullPermission = `pica_service.${permission}`
      } else if (this.scope.type === 'contract-manager') {
        fullPermission = `contract_manager.${permission}`
      } else {
        return false
      }

      return this.hasPerm(fullPermission)
    },

    async fetchList({ filters, pagination, sorting }) {
      const { transaction_type, ...restFilters } = filters
      const mappedFilters = transaction_type
        ? { ...restFilters, source_type: transaction_type }
        : restFilters

      await this.fetchMovementList({
        filters: mappedFilters,
        pagination,
        sorting
      })
    },

    async onExport() {
      this.exporting = true
      try {
        const filters = this.$refs.movementsTable
          ? this.$refs.movementsTable.getFilters()
          : {}

        if (filters.transaction_type) {
          filters.source_type = filters.transaction_type
          delete filters.transaction_type
        }

        if (!filters.date_from) {
          const oneYearAgo = new Date()
          oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1)
          filters.date_from = oneYearAgo.toISOString().split('T')[0]
        }
        if (!filters.date_to) {
          filters.date_to = new Date().toISOString().split('T')[0]
        }

        await this.exportMovements(filters)
      } finally {
        this.exporting = false
      }
    },

    navigateToSource(item) {
      const route = getMovementDetailRouteParams(item.transaction_type, item.source_id, this.scope)
      if (route) {
        this.$router.push(route)
      }
    },

    getTypeLabel(type) {
      return MOVEMENT_TYPE_LABELS[type] || type
    },

    getTypeColor(type) {
      return MOVEMENT_TYPE_COLORS[type] || 'secondary'
    },

    getTypeIcon(type) {
      return MOVEMENT_TYPE_ICON[type] || 'cipInfo'
    }
  }
}
</script>

<style scoped>
.custom-table--clickable >>> tbody tr {
  cursor: pointer;
}
.custom-table--clickable >>> tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.03);
}
</style>
